import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Segmented } from '@/components/ui/segmented';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToastAction } from '@/components/ui/toast';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import {
  getFavouriteDrugs, toggleDrugFavourite, updateDrugPreferences, updateCustomDrug,
  type FavouriteDrug, type DrugSearchResult, type HiddenDrug,
} from '@/lib/api';
import DrugSearchInput from '@/components/shared/DrugSearchInput';
import CompositionEditor from '@/components/shared/CompositionEditor';
import AutocompleteInput from '@/components/shared/AutocompleteInput';
import { getDosageOptions, WHEN_OPTIONS, DURATION_OPTIONS } from '@/lib/constants';
import { useHiddenDrugs, unhideDrug } from '@/hooks/useHiddenDrugs';
import { MoreHorizontal } from 'lucide-react';

type MyMedField = 'dosage' | 'taken_when' | 'duration' | 'notes';
const FIELDS: MyMedField[] = ['dosage', 'taken_when', 'duration', 'notes'];
// drug_catalog.form values; the pad's dose suggestions follow the form
const FORMS = ['tablet', 'capsule', 'syrup', 'suspension', 'injection', 'drops', 'cream', 'ointment', 'inhaler', 'powder', 'other'];

type OwnDetails = { id: number; brand_name: string; composition_raw: string; form: string; justAdded: boolean };

// Columns as the Rx pad's medicine table: Medicine · Dose · When · Duration · Notes · ⋯
const GRID = 'md:grid md:grid-cols-[minmax(0,2fr)_96px_minmax(0,1.1fr)_96px_minmax(0,1.2fr)_32px] md:gap-x-2 md:items-center';
// Reads as text until hovered or focused, as on the pad
const CELL = 'flex items-center min-w-0 h-8 touch:h-10 rounded-md border border-transparent hover:border-field touch:border-field focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20 px-1 transition-colors';

/** Presets → Medicines: the doctor's favourite medicines with their usual dose, timing and duration, and the ones they hid */
export default function MyMedicines({ onChanged }: { onChanged?: () => void }) {
  const [drugs, setDrugs] = useState<FavouriteDrug[] | null>(null);
  // Unsaved edits per drug; a field shows edit ?? my default ?? catalog default (grey)
  const [edits, setEdits] = useState<Record<number, Partial<Record<MyMedField, string>>>>({});
  const [savedId, setSavedId] = useState<number | null>(null);
  const [searchKey, setSearchKey] = useState(0);
  const [view, setView] = useState<'favourites' | 'hidden'>('favourites');
  // A medicine the clinic added itself, open for editing its name, composition and form
  const [editing, setEditing] = useState<OwnDetails | null>(null);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  // Shared with the drug search, so hiding / unhiding anywhere shows up here
  const hidden = useHiddenDrugs();

  const load = useCallback(async () => {
    try { setDrugs(await getFavouriteDrugs()); }
    catch { toast({ title: 'Failed to load your medicines', variant: 'destructive' }); setDrugs([]); }
  }, []);

  // Hiding a medicine also unstars it — reload favourites whenever the hidden list changes
  useEffect(() => { load(); onChanged?.(); }, [load, hidden.length]);
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const mine = (d: FavouriteDrug, f: MyMedField): string | null => d[`custom_${f}`] ?? null;
  const shown = (d: FavouriteDrug, f: MyMedField): string =>
    edits[d.id]?.[f] ?? mine(d, f) ?? (f === 'notes' ? '' : d[`default_${f}`]) ?? '';
  // The catalog's value, not one the doctor set
  const isDefault = (d: FavouriteDrug, f: MyMedField) => edits[d.id]?.[f] === undefined && mine(d, f) == null;

  const save = async (d: FavouriteDrug, row: Partial<Record<MyMedField, string>>) => {
    const v = (f: MyMedField) => ((row[f] ?? mine(d, f) ?? '').trim() || null);
    try {
      await updateDrugPreferences(d.id, {
        custom_dosage: v('dosage'), custom_taken_when: v('taken_when'),
        custom_duration: v('duration'), custom_notes: v('notes'),
      });
      setDrugs(list => list?.map(x => x.id !== d.id ? x : { ...x, ...Object.fromEntries(FIELDS.map(f => [`custom_${f}`, v(f)])) }) ?? null);
      setEdits(e => { const { [d.id]: _, ...rest } = e; return rest; });
      setSavedId(d.id);
      setTimeout(() => setSavedId(id => (id === d.id ? null : id)), 1500);
    } catch { toast({ title: `Couldn't save ${d.brand_name}. Try again.`, variant: 'destructive' }); }
  };

  // Saves a moment after the doctor stops typing — no Save button
  const setField = (d: FavouriteDrug, f: MyMedField, value: string) => {
    setEdits(e => {
      const row = { ...e[d.id], [f]: value };
      clearTimeout(timers.current[d.id]);
      timers.current[d.id] = setTimeout(() => save(d, row), 800);
      return { ...e, [d.id]: row };
    });
  };

  const add = async (drug: DrugSearchResult) => {
    try {
      // The dropdown star may already have added it — only star if it isn't in the list yet
      const current = await getFavouriteDrugs();
      if (!current.some(d => d.id === drug.id)) await toggleDrugFavourite(drug.id);
      toast({ title: `${drug.brand_name} added` });
      setSearchKey(k => k + 1);
      load(); onChanged?.();
      // Just typed in as a new medicine: ask for the composition, which prints as the generic name
      if (drug.is_own && !drug.composition_raw) edit(drug, true);
    } catch { toast({ title: 'Failed to add medicine', variant: 'destructive' }); }
  };

  const edit = (d: DrugSearchResult, justAdded = false) => {
    setEditError('');
    setEditing({ id: d.id, brand_name: d.brand_name, composition_raw: d.composition_raw ?? '', form: d.form || 'tablet', justAdded });
  };

  const saveDetails = async () => {
    if (!editing) return;
    if (!editing.brand_name.trim()) { setEditError('Enter a name'); return; }
    setEditSaving(true);
    try {
      await updateCustomDrug(editing.id, { brand_name: editing.brand_name, composition_raw: editing.composition_raw, form: editing.form });
      setEditing(null);
      load();
    } catch (x: any) { setEditError(x?.message || 'Failed to save'); }
    finally { setEditSaving(false); }
  };

  // Retires it for the whole clinic — past prescriptions keep the name they were written with
  const deleteOwn = async (d: FavouriteDrug) => {
    try {
      await updateCustomDrug(d.id, { is_active: false });
      load(); onChanged?.();
      toast({
        title: `${d.brand_name} deleted`,
        description: "It no longer shows in your clinic's medicine search.",
        duration: 6000,
        action: <ToastAction altText="Undo" onClick={async () => { await updateCustomDrug(d.id, { is_active: true }); load(); onChanged?.(); }}>Undo</ToastAction>,
      });
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const remove = async (d: FavouriteDrug) => {
    try {
      await toggleDrugFavourite(d.id);
      load(); onChanged?.();
      toast({
        title: `${d.brand_name} removed from your medicines`,
        duration: 6000,
        action: <ToastAction altText="Undo" onClick={async () => { await toggleDrugFavourite(d.id); load(); onChanged?.(); }}>Undo</ToastAction>,
      });
    } catch { toast({ title: 'Failed to remove', variant: 'destructive' }); }
  };

  const unhide = async (d: HiddenDrug) => {
    try {
      await unhideDrug(d.id);
      toast({ title: `${d.brand_name} shows in your search again` });
    } catch { toast({ title: 'Failed to unhide', variant: 'destructive' }); }
  };

  const cell = (d: FavouriteDrug, f: MyMedField, label: string) => (
    <div className="min-w-0">
      <span className="md:hidden block text-2xs text-label mb-0.5">{label}</span>
      <div className={CELL}>
        {f === 'notes' ? (
          <input
            value={shown(d, f)} placeholder="Add a note" aria-label={`${d.brand_name} ${label}`}
            onChange={e => setField(d, f, e.target.value)}
            className="w-full bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
          />
        ) : (
          <AutocompleteInput
            value={shown(d, f)} showAllOnFocus
            options={f === 'dosage' ? getDosageOptions(d) : f === 'taken_when' ? WHEN_OPTIONS : DURATION_OPTIONS}
            onChange={v => setField(d, f, v)}
            placeholder={label}
            className={cn('text-sm py-0 px-0', f === 'dosage' && 'font-mono tabular-nums', isDefault(d, f) && 'text-muted-foreground')}
            containerClassName="px-0 flex-1 min-w-0"
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Shown first when you prescribe, filled in with your dose, timing and duration.</p>

      <div className="flex flex-wrap items-center gap-2">
        {view === 'favourites' ? (
          <div className="flex-1 min-w-[220px] flex items-center h-8 touch:h-10 rounded-md border border-dashed border-field bg-card px-2.5 focus-within:border-ring focus-within:border-solid focus-within:ring-2 focus-within:ring-ring/20">
            <div className="flex-1 min-w-0">
              <DrugSearchInput key={searchKey} value="" onChange={add} placeholder="Type a medicine to add, brand or generic…" className="text-sm font-normal py-0 px-0" />
            </div>
          </div>
        ) : <div className="flex-1" />}
        <Segmented
          aria-label="Show"
          value={view}
          onChange={v => setView(v as 'favourites' | 'hidden')}
          options={[
            { value: 'favourites', label: <>Favourites <span className="tabular-nums text-muted-foreground">{drugs?.length ?? 0}</span></> },
            { value: 'hidden', label: <>Hidden <span className="tabular-nums text-muted-foreground">{hidden.length}</span></> },
          ]}
        />
      </div>

      {view === 'hidden' ? (
        hidden.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No hidden medicines. Use the eye icon in the prescription's medicine search to hide one you never prescribe.
          </p>
        ) : (
          <div className="rounded-lg border border-border bg-card divide-y divide-divider">
            {hidden.map(d => (
              <div key={d.id} className="flex items-center justify-between gap-3 px-3 min-h-10 py-1.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{d.brand_name}</div>
                  {d.composition_raw && <div className="text-xs text-muted-foreground truncate">{d.composition_raw}</div>}
                </div>
                <Button size="sm" variant="outline" className="h-8 text-xs shrink-0" onClick={() => unhide(d)}>Unhide</Button>
              </div>
            ))}
          </div>
        )
      ) : drugs === null ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
      ) : drugs.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Add the medicines you prescribe most. You can also star one in the prescription's medicine search.
        </p>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className={cn('hidden h-8 px-3 bg-muted/40 border-b border-border text-xs font-medium text-label', GRID)}>
            <span>Medicine</span><span className="px-1">Dose</span><span className="px-1">When</span><span className="px-1">Duration</span><span className="px-1">Notes</span><span />
          </div>
          <div className="divide-y divide-divider">
            {drugs.map(d => (
              <div key={d.id} className={cn('px-3 py-2 md:py-1', GRID)}>
                <div className="flex items-start gap-2 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {d.brand_name}
                      {!!d.is_own && <span className="ml-1.5 align-middle inline-flex h-4 items-center rounded border border-border px-1 text-2xs font-normal text-muted-foreground" title="Added by your clinic">Own</span>}
                      {savedId === d.id && <span className="ml-2 text-xs font-normal text-success">Saved</span>}
                    </div>
                    {d.composition_raw ? <div className="text-xs text-muted-foreground truncate">{d.composition_raw}</div>
                      : !!d.is_own && <button type="button" onClick={() => edit(d)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">+ Add composition</button>}
                  </div>
                  <div className="md:hidden">{menu(d)}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-1.5 md:contents">
                  {cell(d, 'dosage', 'Dose')}
                  {cell(d, 'taken_when', 'When')}
                  {cell(d, 'duration', 'Duration')}
                  {cell(d, 'notes', 'Notes')}
                </div>
                <div className="hidden md:block">{menu(d)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {view === 'favourites' && !!drugs?.length && (
        <p className="text-xs text-muted-foreground">Grey values come from the catalog until you change them. Changes save as you type.</p>
      )}

      <Dialog open={!!editing} onOpenChange={v => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Medicine details</DialogTitle>
            <DialogDescription>
              {editing?.justAdded
                ? `${editing.brand_name} is in your medicines. Add what it contains so it prints with its generic name and allergy checks can match it.`
                : 'Changes apply to new prescriptions for everyone in your clinic. Past prescriptions stay as written.'}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <form className="space-y-4 py-1" onSubmit={e => { e.preventDefault(); saveDetails(); }}>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_136px]">
                <div className="space-y-1">
                  <Label htmlFor="own-med-name">Name</Label>
                  <Input id="own-med-name" value={editing.brand_name}
                    onChange={e => { setEditing({ ...editing, brand_name: e.target.value }); setEditError(''); }} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="own-med-form">Form</Label>
                  <Select value={editing.form} onValueChange={v => { setEditing({ ...editing, form: v }); setEditError(''); }}>
                    <SelectTrigger id="own-med-form" className="capitalize"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FORMS.map(f => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="own-med-composition">Composition</Label>
                <CompositionEditor key={editing.id} id="own-med-composition" value={editing.composition_raw}
                  autoFocus={!editing.composition_raw}
                  onChange={v => setEditing(e => (e ? { ...e, composition_raw: v } : e))} onDone={saveDetails} />
              </div>
              {editError && <p className="text-xs text-destructive">{editError}</p>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={editSaving}>Cancel</Button>
                <Button type="submit" disabled={editSaving}>{editSaving ? 'Saving…' : 'Save'}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  function menu(d: FavouriteDrug) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 touch:h-10 touch:w-10 p-0 text-muted-foreground" aria-label={`More for ${d.brand_name}`}>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!!d.is_own && <DropdownMenuItem onClick={() => edit(d)}>Edit details</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => remove(d)}>Remove from my medicines</DropdownMenuItem>
          {!!d.is_own && (<>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => deleteOwn(d)}>Delete from clinic</DropdownMenuItem>
          </>)}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
}
